import { isEmpty, pick } from 'lodash';
import { InvoiceSummaryBlock } from '~/models/blocks/invoice-summary-block.model';
import { ServicesBlock } from '~/models/blocks/services-block.model';
import { InvoiceColumnHeaderBlock } from '~/models/blocks/invoice-column-header-block.model';
import {
    IMobileAppService,
    InvoiceAppearanceExtendedServices,
    InvoiceMobileBaseProps,
    InvoiceSummaryMobileProps,
    MobileAutoPayData,
    MobileInvoiceBlockData,
    PaymentScheduleMobileEntity
} from '~/components/Builder/Mobile/builderMobileApi.types';
import { v4 as uuid } from 'uuid';
import { Page } from '~/models/page.model';
import { ContractBlock } from '~/models/blocks/contract-block.model';
import { ServiceItemBlock } from '~/models/blocks/service-item-block.model';
import { ServiceSelectionItemBlock } from '~/models/blocks/service-selection-item-block.model';
import { ServiceItemKeys } from '~/components/common/InvoiceBlock/ServiceItemBlock.utils';
import { INITIAL_SERVICE_ITEM_VALUES } from '~/components/Builder/Block/ServicesBlock/service-block.utils';
import { Flow } from '~/models/flow.model';
import { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import { PaymentScheduleBlock } from '~/models/blocks/payment-schedule-block.model';
import type { PaymentBlock as PaymentBlockModel } from '~/models/blocks/payment-block.model';
import { getPaymentDateDisplayValue } from '~/components/common/InvoiceBlock/invoice.utils';
import { toFixedIfNotRound } from '~/utils/money.utils';
import { TBlock } from '~/types/builder.types';
import { CoreOutboundEvents } from '~/components/Builder/Mobile/builderMobileApi.enums';
import { PaymentStatus } from '~/types/model.types';
import {
    getPaymentStatusTooltipData,
    getAutoPayTooltipText
} from '~/components/Builder/Block/PaymentScheduleBlock/AutoPay.utils';
import i18next from 'i18next';
import { CHARGE_BLOCK_PROPERTIES } from '~/components/Builder/Block/ChargeBlock/ChargeBlockCommon';
import { ChargeBlock } from '~/models/blocks/charge-block.model';
import { forbidMoreThanMaxUploadQuestionBlocksForCompositionBlock } from '~/components/Builder/Common/CreationLimitations/questionLimitations';
import {
    Allowed,
    Disabled
} from '~/components/Builder/Common/CreationLimitations/creationLimitations.types';
import { IInvoiceSummaryBlockResponse } from '~/types/server.types';

export function getServicesBlockData({
    servicesBlock,
    invoiceColumnsBlock,
    invoiceSummaryBlock,
    chargeBlock,
    isRecurringInvoice,
    isRecurringUnder10Days
}: {
    servicesBlock: ServicesBlock;
    invoiceColumnsBlock: InvoiceColumnHeaderBlock;
    invoiceSummaryBlock: InvoiceSummaryBlock;
    chargeBlock: ChargeBlock;
    isRecurringInvoice: boolean;
    isRecurringUnder10Days: boolean;
}) {
    const servicesBlockData: InvoiceAppearanceExtendedServices = {
        ...servicesBlock.getDataForServer()
    };
    servicesBlockData.invoice_column_header_block = pick(
        invoiceColumnsBlock,
        InvoiceMobileBaseProps
    );
    servicesBlockData.invoice_summary_block = pick(
        invoiceSummaryBlock,
        InvoiceSummaryMobileProps
    );

    servicesBlockData.chargeBlock = {
        ...pick(chargeBlock, [...CHARGE_BLOCK_PROPERTIES]),
        ...{
            isRecurring: isRecurringInvoice,
            isRecurringUnderMinimumDays: isRecurringUnder10Days
        }
    };
    return servicesBlockData;
}

export function getPagesProperties(selectedFlow: Flow) {
    return selectedFlow.pages.map((page: Page) => {
        let isDeleteDisabledValue = isDeleteDisabledOnPage(selectedFlow, page);
        const contractContainerBlock = page.contractBlock;
        let duplicationDisabledReason = '';
        const duplicationStatus: Allowed | Disabled =
            forbidMoreThanMaxUploadQuestionBlocksForCompositionBlock({
                sourceBlock: page.rootBlock,
                targetFlow: selectedFlow
            });
        if (duplicationStatus.status === 'disabled') {
            duplicationDisabledReason = duplicationStatus.reason;
        }
        return {
            ...page.getDataForServer(),
            background_image: page.background_image,
            styles: page.styles,
            isPaymentPage: page.isPaymentPage,
            // isPayContainerBlock will be removed gradually safely
            isPayContainerBlock: !!page.payContainerBlock,
            isInvoiceContainerBlock: !!page.invoiceContainerBlock,
            isScheduleContainerBlock: page.isSchedulePage,
            isSchedulePageWithFutureMeeting:
                page.scheduleContainerBlock?.isFutureMeeting,
            isSchedulePageWithPastMeeting:
                page.scheduleContainerBlock?.isPastMeeting,
            isSchedulePageIsDefault: page.scheduleContainerBlock?.isDefault,
            isSchedulePageIsRequireToPay:
                page.scheduleContainerBlock?.isRequireToPay,
            scheduleSessionName: page.scheduleContainerBlock?.sessionName,
            isValid: !page.hasErrors,
            isDeleteDisabled: !!isDeleteDisabledValue,
            isContractContainerBlock: !!contractContainerBlock,
            isDuplicateAllowed: duplicationStatus.status === 'allowed',
            duplicationDisabledReason,
            isContractRecaps:
                !!contractContainerBlock &&
                (contractContainerBlock as ContractBlock)?.hasRecaps,
            isServiceSelectionContainerBlock: !isEmpty(
                page.serviceSelectionBlocks
            )
        };
    });
}

export function getSinglePaymentData({
    paymentId,
    selectedFlow,
    paymentScheduleBlock
}: {
    paymentId: string;
    selectedFlow: Flow;
    paymentScheduleBlock: PaymentScheduleBlock;
}) {
    const blockData = {};
    setPaymentScheduleBlockData({
        blockData,
        selectedFlow,
        paymentScheduleBlock,
        singlePaymentIdToFilter: paymentId
    });
    return { blockData };
}

export function setPaymentScheduleBlockData({
    blockData,
    selectedFlow,
    paymentScheduleBlock,
    singlePaymentIdToFilter = ''
}: {
    blockData: Partial<PaymentScheduleMobileEntity>;
    selectedFlow: Flow;
    paymentScheduleBlock: PaymentScheduleBlock;
    singlePaymentIdToFilter?: string;
}) {
    const t = i18next.getFixedT(null, 'builder');
    const invoiceBlock = paymentScheduleBlock.parent as InvoiceBlock;
    blockData.chargeBlockId = selectedFlow.chargeContainerBlock.block_id;
    blockData.isPublished = selectedFlow.is_published;
    blockData.currency = selectedFlow.currency;
    blockData.isPaymentScheduleBlockDisabled =
        invoiceBlock.hasServicesSelectionBlocks ||
        invoiceBlock.isRecurringInvoice;
    blockData.grandTotal = Number(
        toFixedIfNotRound(invoiceBlock.invoiceSummaryBlock.grandTotal)
    );
    // get the errors (mobile formatted), pick the active one
    blockData.errorMessage = paymentScheduleBlock.mobileErrorMessages.find(
        error => error.enabled
    )?.message;
    blockData.totalNumberOfPayments =
        paymentScheduleBlock.totalNumberOfPayments;
    blockData.invoice_summary_block = getInvoiceSummaryBlockData(
        selectedFlow.invoiceSummaryBlock
    );
    blockData.paymentBlocks = (
        paymentScheduleBlock as PaymentScheduleBlock
    ).paymentBlocks
        .map((paymentBlock: PaymentBlockModel, index: number) => {
            const {
                payment_content,
                connectedVariable,
                paymentDueDate: date,
                paymentStatus,
                isManualPaid,
                isEligibleForReminder,
                wasReminderSent
            } = paymentBlock;
            const bottomTranslationText =
                getPaymentStatusTooltipData(paymentBlock)?.text;
            const statusBottomText = t(bottomTranslationText);

            const autoPayData: MobileAutoPayData = paymentBlock.isAutoPay
                ? pick(
                      getAutoPayTooltipText(
                          paymentBlock,
                          paymentBlock.invoiceBlock.autoPayStatus,
                          paymentBlock.flow.autoPayClientName
                      ),
                      ['title', 'description']
                  )
                : null;
            const shouldShowSendReminder =
                isEligibleForReminder && !wasReminderSent;

            const dueDateValue = getPaymentDateDisplayValue({
                date,
                paymentVariable: connectedVariable?.value,
                paymentContent: payment_content,
                isRecurring: paymentBlock.invoiceBlock.isRecurringInvoice,
                isTemplate: paymentBlock.flow.is_template
            });
            return {
                blockId: paymentBlock.block_id,
                paymentId: paymentBlock.paymentId,
                paymentIndex: index + 1,
                isDeletable: paymentBlock.isDeletable,
                paymentStatus,
                amount: paymentBlock.paymentAmount,
                amountCalcType: paymentBlock.amountCalcType,
                remainedMaxCustom: paymentBlock.remainedMaxCustom,
                dateCalcType: paymentBlock.payment_content.date_calc_type,
                percentage: paymentBlock.payment_content.percentage,
                refundedAmount: paymentBlock.refundedAmount,
                isEditable:
                    !paymentBlock.isPaid &&
                    !paymentBlock.isRefunded &&
                    !paymentScheduleBlock.isRecurringInvoice,
                dueDate: dueDateValue,
                smartDatePeriod: paymentBlock.payment_content.smart_date_period,
                smartDatePeriodType:
                    paymentBlock.payment_content.smart_date_period_type,
                smartDatePeriodRelation:
                    paymentBlock.payment_content.smart_date_period_relation,
                isMilestone: paymentBlock.isMilestone,
                milestoneDetails: paymentBlock.isMilestone
                    ? {
                          completedDate: paymentBlock.milestoneCompletedOn,
                          remindersList: paymentBlock.reminders?.map(
                              reminder => reminder.sent_on
                          )
                      }
                    : {},
                isAutoPay: paymentBlock.isAutoPay,
                autoPay: autoPayData,
                bottomText: statusBottomText,
                shouldShowMarkAsPaid: paymentBlock.shouldShowMarkAsPaid,
                shouldShowViewReceipt: paymentBlock.isPaymentHandled,
                shouldShowUndoMarkAsPaid:
                    paymentStatus === PaymentStatus.Paid && isManualPaid,
                shouldShowMarkAsRefunded:
                    (paymentStatus === PaymentStatus.PartiallyRefunded &&
                        paymentBlock.isManualPaid) ||
                    (paymentStatus === PaymentStatus.Paid && isManualPaid),
                shouldShowSendReminder:
                    shouldShowSendReminder && !wasReminderSent
            };
        })
        .filter(payBlock => {
            if (!!singlePaymentIdToFilter) {
                return payBlock.paymentId === singlePaymentIdToFilter;
            } else {
                return true;
            }
        });
}

export const setPropsOnInvoiceBlock = (
    blockData: MobileInvoiceBlockData,
    showInvoiceOverlayForId: string,
    block: InvoiceBlock,
    selectedFlow: Flow
) => {
    blockData.isBlockOverlayOn = showInvoiceOverlayForId === block.block_id;
    blockData.isRecurring = (block as InvoiceBlock).isRecurringInvoice;
    blockData.isDeleteDisabled =
        selectedFlow.invoiceContainerBlock?.isDeleteDisabled;
    blockData.isRecurringUnderMinimumDays = (
        block as InvoiceBlock
    ).isRecurringUnder10Days;
    blockData.chargeBlock = pick((block as InvoiceBlock).chargeBlock, [
        ...CHARGE_BLOCK_PROPERTIES
    ]);
    blockData.chargeContainerBlockId =
        selectedFlow.chargeContainerBlock.block_id;
    blockData.invoice_summary_block = getInvoiceSummaryBlockData(
        block.invoiceSummaryBlock
    );
    blockData.invoice_column_header_block = pick(
        block.invoiceColumnHeaderBlock,
        InvoiceMobileBaseProps
    );
    setTaxedServicesOnInvoiceBlock({ blockData, selectedFlow });
};

function getInvoiceSummaryBlockData(
    block: InvoiceSummaryBlock
): Partial<IInvoiceSummaryBlockResponse> {
    return {
        block_id: block.block_id,
        page_id: block.page_id,
        type: block.type,
        styles: block.styles,
        invoice_summary: {
            ...block.invoice_summary,
            is_client_payment_plan: block.isClientPaymentPlan,
            max_number_of_payments: block.maxNumberOfPayments,
            first_payment_due_date: block.firstPaymentDueDate,
            allow_pay_in_full_discount: block.allowPayInFullDiscount,
            pay_in_full_discount: block.payInFullDiscount,
            pay_in_full_discount_type: block.payInFullDiscountType,
            first_payment_due_date_type: block.firstPaymentDueDateType
        }
    };
}

export const setTaxedServicesOnInvoiceBlock = ({
    blockData,
    selectedFlow
}: {
    blockData: MobileInvoiceBlockData;
    selectedFlow: Flow;
}) => {
    [1, 2, 3].map(tax_number => {
        const taxName = `tax_${tax_number}`;
        if (blockData.invoice_summary_block.invoice_summary[taxName]) {
            blockData.invoice_summary_block.invoice_summary[
                taxName
            ].taxedItems =
                selectedFlow.invoiceContainerBlock.invoiceServicesBlock.countServiceTaxed(
                    taxName
                );
            delete blockData.invoice_summary_block.invoice_summary[taxName]
                ?._id;
            delete blockData.invoice_summary_block.invoice_summary[taxName]
                ?.updated_at;
            delete blockData.invoice_summary_block.invoice_summary[taxName]
                ?.created_at;
        } else {
            blockData.invoice_summary_block.invoice_summary[taxName] = {
                active: false,
                label: '',
                tax_id: uuid(),
                type: 'relative',
                value: 0,
                taxedItems: 0
            };
        }
    });
};

export const setTaxesPropsOnSummaryBlock = ({
    summaryBlock,
    selectedFlow
}: {
    summaryBlock: Partial<InvoiceSummaryBlock>;
    selectedFlow: Flow;
}) => {
    [1, 2, 3].map(tax_number => {
        const taxName = `tax_${tax_number}`;
        summaryBlock.invoice_summary[taxName].taxedItems =
            selectedFlow.invoiceContainerBlock.invoiceServicesBlock.countServiceTaxed(
                taxName
            );
        delete summaryBlock.invoice_summary[taxName]?._id;
        delete summaryBlock.invoice_summary[taxName]?.updated_at;
        delete summaryBlock.invoice_summary[taxName]?.created_at;
    });
};

export const isDeleteDisabledForBlock = (selectedFlow: Flow): boolean => {
    return !!selectedFlow.invoiceContainerBlock?.invoicePaymentScheduleBlock
        ?.isDeleteDisabled;
};

export const isDeleteDisabledOnPage = (
    selectedFlow: Flow,
    selectedPage: Page
): boolean => {
    const isSinglePage = selectedFlow && selectedFlow.pages.length === 1;
    const { invoiceContainerBlock, payContainerBlock } = selectedPage;
    const hasInvoiceOrPayBlock = invoiceContainerBlock || payContainerBlock;
    const invoiceDeleteDisabled =
        hasInvoiceOrPayBlock &&
        selectedFlow.invoiceContainerBlock?.isDeleteDisabled;
    const { contractBlock } = selectedPage;
    const contractDeleteDisabled = (contractBlock as ContractBlock)
        ?.hasClientSignatures;
    return isSinglePage || invoiceDeleteDisabled || contractDeleteDisabled;
};

export function getServiceItemForMobile(block: ServiceItemBlock) {
    // consider adding scope to this check
    const isServiceSelectionInInvoice =
        block instanceof ServiceSelectionItemBlock &&
        block.invoice_service_item;
    const currentServiceItem = pick(
        isServiceSelectionInInvoice
            ? (block as ServiceSelectionItemBlock).invoice_service_item
            : block.service_item,
        ServiceItemKeys
    );

    return {
        ...INITIAL_SERVICE_ITEM_VALUES,
        ...currentServiceItem
    };
}

export function sendPaymentUpdateBlock(
    selectedFlow: Flow,
    selectedBlock: TBlock,
    mobileAppService: IMobileAppService
) {
    const scheduleBlockForUpdate = {
        ...selectedFlow.paymentScheduleBlock.getDataForServer()
    };
    setPaymentScheduleBlockData({
        blockData: scheduleBlockForUpdate,
        selectedFlow,
        paymentScheduleBlock: selectedBlock as PaymentScheduleBlock
    });
    mobileAppService.triggerAppEvent(CoreOutboundEvents.UpdateBlockData, {
        block: scheduleBlockForUpdate
    });
}
